import React, { useState, useCallback, useContext, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Collapse, NavbarBrand, NavItem, UncontrolledTooltip } from 'src/components/ui';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import sortBy from 'lodash/sortBy';

import { GroupDefinition, TabDefinition, TabsDefinition, TabGroup } from './types';
import { useTabGrouping } from './useTabGrouping';
import useStoredState from '../../hooks/useStoredState';
import useUID from '../../util/useUID';
import UWEContext from '../../context/UWEContext';
import { useRouterContext } from '../../context/RouterContext';
import { useConfiguration } from '../../context/ConfigurationContext';
import getSystemSettingTabs from '../../views/SystemSettings/tabs';
import Jnx from '../../util/jnx';
import { Logo } from '../_general/Logo';
import Clock from '../Clock';
import UserIcon from '../UserIcon';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { Trans, useLingui } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { UserMenuItems } from '../UserDropdown';
import { useDarkMode } from '../../context/DarkModeContext';
import { setLanguage, isCurrentLanguage } from '../../i18n';
import { useDebouncedEffect } from '../../hooks/useDebounceEffect';
import { AuthContextValue, UserStoreUser } from '../../context/AuthContext';
import { AccessProfileDTO } from '../../api/AuthApi';
import { useTheme } from '@emotion/react';
import Icon from 'src/components/Icon';
import { isTicketsAllowedEnvironment } from '../../util/environment';
import { isOcifOrganizationalUnit, getActiveOrganizationalUnitTenantId } from '../../util/organizationalUnit';

interface SidebarTabDef {
  name?: string;
  title?: string;
  icon?: string;
  path?: string;
  order?: number;
  noTab?: boolean;
  isAdmin?: boolean;
  require?: string;
  permission?: string;
  jnx?: string;
  group?: string;
}
export type SidebarTabs = Record<string, SidebarTabDef>;

// Props accepted by the Sidebar component
interface SidebarProps {
  tabs?: SidebarTabs;
  tabGroups?: Record<string, GroupDefinition>;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  isCollapsed: boolean;
  toggleSidebar: () => void;
  user: UserStoreUser | null | undefined;
  auth: AuthContextValue;
  /** Shows the nav-tab skeleton rows instead of the real tab list (e.g. while workpages are still loading). */
  isLoading?: boolean;
}

// Props for the SidebarUserPortalDropdown helper component
interface SidebarUserPortalDropdownProps {
  user: UserStoreUser | null | undefined;
  auth: AuthContextValue;
  children: any;
}

// Built-in access profiles have a stable `name` and a translatable label; custom, tenant-created
// profiles (e.g. "Supervisor de Analisis Financiero") don't map to anything in the catalog, so
// those fall back to the `displayName` the backend already sent.
const ACCESS_PROFILE_LABELS: Record<string, () => string> = {
  administrator: () => t`Administrator`,
  user: () => t`User`,
  systemadmin: () => t`System Administrator`,
  useradmin: () => t`User Admin`,
};

function getAccessProfileLabel(accessProfile?: AccessProfileDTO | null): string | undefined {
  if (!accessProfile) return undefined;
  return (ACCESS_PROFILE_LABELS[accessProfile.name] ?? (() => accessProfile.displayName))();
}

function SidebarUserPortalDropdown({ user, auth, children }: SidebarUserPortalDropdownProps): React.ReactElement | null {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ bottom: 0, left: 0 });

  const open = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown: (e: MouseEvent) => void = e => {
      if (!triggerRef.current?.contains(e.target as Node) && !menuRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  return (
    <>
      <div ref={triggerRef} style={{ cursor: 'pointer', padding: '4px', display: 'flex', gap: 8 }} onClick={isOpen ? () => setIsOpen(false) : open}>
        <UserIcon user={user?.user} color={auth?.avatarColor} size="30px" />
        {children ? children : null}
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="user-dropdown-menu p-0 shadow"
            style={{ position: 'fixed', bottom: pos.bottom, left: pos.left, zIndex: 1100, minWidth: '220px' }}
            onClick={e => {
              if ((e.target as Element).closest('.dropdown-item')) setIsOpen(false);
            }}
          >
            <UserMenuItems showHeader={false} showFaq={false} />
          </div>,
          document.body
        )}
    </>
  );
}

function Sidebar(props: SidebarProps): React.ReactElement | null {
  const {
    sidebarTab: selected,
    setSelected,
    tabs,
    isCollapsed,
    user,
    auth,
    history,
    isLoading,
    hasAdminWorkpages,
    isSettingsActive,
    isSystemSettingsRoute,
    isFaqActive,
    activeTabGroups,
  } = useSidebarHooks(props);
  const { instance: msalInstance } = useMsal();

  const { tabGroups, ungroupedTabs } = useTabGrouping({ tabs: (tabs ?? {}) as TabsDefinition, groups: activeTabGroups });

  const permissions = ((auth || {}).user || {}).permissions || {};
  const manageAnything = permissions.ManageUsersInUnit || permissions.ManageOrganizationalUnits || hasAdminWorkpages;
  const showTickets = isTicketsAllowedEnvironment() && isOcifOrganizationalUnit(auth?.user);
  const publicDashboardsTenantId = getActiveOrganizationalUnitTenantId(auth?.user) ?? 1;

  const [openUserCard, setOpenUserCard] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => ['en', 'es'].find(l => isCurrentLanguage(l)) ?? 'en');
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [fullyExpanded, setFullyExpanded] = useState(!isCollapsed);
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentTabKeys = useMemo(
    () =>
      Object.keys(tabs ?? {})
        .sort()
        .join(','),
    [tabs]
  );

  // Initialise from the module-level snapshot so the value is correct on mount
  // even if an earlier (loading-phase) Sidebar already updated the module variable.
  const [shouldAnimateTabs, setShouldAnimateTabs] = useState(
    () => currentTabKeys !== '' && currentTabKeys !== (localStorage.getItem('sidebar:workpage-keys') ?? '')
  );

  useDebouncedEffect(
    () => {
      if (!currentTabKeys) return;
      if (currentTabKeys !== (localStorage.getItem('sidebar:workpage-keys') ?? '')) {
        localStorage.setItem('sidebar:workpage-keys', currentTabKeys);
        setShouldAnimateTabs(true);
      }
    },
    [currentTabKeys],
    100
  );

  useEffect(() => {
    if (!isCollapsed) {
      expandTimerRef.current = setTimeout(() => setFullyExpanded(true), 350);
    } else {
      if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
      setFullyExpanded(false);
    }
    return () => {
      if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
    };
  }, [isCollapsed]);

  const isMobile = useIsMobile();
  const isMobileRef = useRef<boolean>(isMobile);
  const prevCollapsedFullView = useRef<boolean>(isMobile ? true : isCollapsed);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!menuRef.current) return;
    const active = menuRef.current.querySelector('.mui-menu-item.active');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  useEffect(() => {
    // Collapse sidebar if screen shrinks to mobile view
    if (isMobile && !isMobileRef.current) {
      prevCollapsedFullView.current = isCollapsed;

      if (!isCollapsed) {
        props.toggleSidebar();
      }
    }

    // Go back to previous state if screen is big enough
    if (!isMobile && isMobileRef.current && isCollapsed !== prevCollapsedFullView.current) {
      props.toggleSidebar();
    }

    isMobileRef.current = isMobile;
  }, [isMobile, isCollapsed, props.toggleSidebar]);

  const toggle = () => setOpenUserCard(prev => !prev);

  const handleLanguage = (lang: string) => {
    setLanguage(lang);
    setCurrentLang(lang);
  };

  const { firstName, lastName, accessProfile } = user?.user || {};

  const sidebarTabs = useMemo(() => {
    const sortedUngrouped = sortBy(ungroupedTabs, ['order']);
    const sortedGroups = sortBy(tabGroups, ['order']);
    let animIndex = 0;
    return (
      <>
        {sortedUngrouped.map(tabDef => (
          <TabEntry
            key={tabDef.name}
            tabKey={tabDef.slug ?? tabDef.name}
            tabDef={tabDef}
            isCollapsed={isCollapsed}
            selectedTab={selected}
            setSelectedTab={setSelected}
            animationIndex={animIndex++}
          />
        ))}
        {sortedGroups.map(tabGroup => {
          const start = animIndex;
          animIndex += 1 + tabGroup.buttons.length;
          return (
            <SidebarTabGroup
              key={tabGroup.name}
              name={tabGroup.name}
              tabGroup={tabGroup}
              isCollapsed={isCollapsed}
              selectedTab={selected}
              setSelectedTab={setSelected}
              startAnimIndex={start}
            />
          );
        })}
      </>
    );
  }, [tabGroups, ungroupedTabs, isCollapsed, selected, setSelected]);

  return (
    <>
      <div className={`mui-sidebar ${isCollapsed ? 'mui-collapsed' : ''} ${fullyExpanded ? 'mui-expanded' : ''}`}>
        <div className="mui-sidebar-header">
          <NavbarBrand className="nav-logo-icon" tag={Link} to="/">
            <div className="sidebar-logo-wrapper">
              <Logo height={isCollapsed ? 30 : 40} width={'auto'} />
              {isSystemSettingsRoute && (
                <span className="sidebar-logo-badge">
                  <Icon icon="fa fa-cog" />
                </span>
              )}
            </div>
            <Clock className="ml-2 nav-clock" />
          </NavbarBrand>
        </div>
        {isMobile && (
          <div className="mui-sidebar-footer mui-sidebar-footer-mobile">
            <div className="mui-sidebar-footer-top" onClick={toggle}>
              <div className="mui-sidebar-user-widget">
                <UserIcon user={user?.user} color={auth?.avatarColor} size="34px" />
                <div className="mui-sidebar-user-details">
                  <span className="mui-sidebar-user-name">
                    {firstName || ''} {lastName || ''}
                  </span>
                  <small className="mui-sidebar-user-role">{getAccessProfileLabel(accessProfile)}</small>
                </div>
              </div>
              <Icon icon="fa fa-angle-down" className={`mui-sidebar-chevron ${openUserCard ? 'flipped' : ''}`} />
            </div>
            <Collapse isOpen={openUserCard}>
              <div className="mui-sidebar-footer-actions">
                <button className="mui-sidebar-footer-action-btn" onClick={() => history.push('/profile')}>
                  <Icon icon="User" />
                  <span>
                    <Trans>My Account</Trans>
                  </span>
                </button>
                <div className="mui-sidebar-footer-toggles">
                  <div className="flex items-center">
                    <span className="small text-muted">
                      <Trans>Language</Trans>
                    </span>
                    <div className="btn-group btn-group-sm ml-auto">
                      {['en', 'es'].map(lang => (
                        <button
                          key={lang}
                          className={`btn ${currentLang === lang ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ minWidth: '2.5rem', fontWeight: 600, fontSize: '0.75rem' }}
                          onClick={() => handleLanguage(lang)}
                        >
                          {lang.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center mt-2">
                    <span className="small text-muted">
                      <Trans>Theme</Trans>
                    </span>
                    <div className="btn-group btn-group-sm ml-auto">
                      <button
                        type="button"
                        className={`btn ${!isDarkMode ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ minWidth: '2.5rem' }}
                        onClick={e => {
                          e.stopPropagation();
                          if (isDarkMode) toggleDarkMode();
                        }}
                      >
                        <Icon icon="Sun" />
                      </button>
                      <button
                        type="button"
                        className={`btn ${isDarkMode ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ minWidth: '2.5rem' }}
                        onClick={e => {
                          e.stopPropagation();
                          if (!isDarkMode) toggleDarkMode();
                        }}
                      >
                        <Icon icon="Moon" />
                      </button>
                    </div>
                  </div>
                </div>
                {user?.impersonator && (
                  <button className="mui-sidebar-footer-action-btn" onClick={() => auth.stopImpersonation()}>
                    <Icon icon="UserMinus" />
                    <span>
                      <Trans>Stop impersonating</Trans>
                    </span>
                  </button>
                )}
                <button
                  className="mui-sidebar-footer-action-btn text-danger"
                  onClick={() => {
                    history.push('/login?skipRedirect=1');
                    auth.logout({ msalInstance });
                  }}
                >
                  <Icon icon="SignOut" weight="regular" size={18} />
                  <span>
                    <Trans>Log Out</Trans>
                  </span>
                </button>
              </div>
            </Collapse>
            <div className="mui-sidebar-mobile-links">
              {manageAnything && (
                <Link className="mui-menu-item" style={{ outline: 'none' }} to="/system-settings" onClick={() => props.toggleSidebar()}>
                  <span className="mui-icon">
                    <Icon icon="Gear" size={18} />
                  </span>
                  <span className="mui-text">
                    <Trans>Configuration</Trans>
                  </span>
                </Link>
              )}
              <Link
                className="mui-menu-item"
                style={{ outline: 'none' }}
                to={`/anonymous/${publicDashboardsTenantId}/dashboards`}
                onClick={() => props.toggleSidebar()}
              >
                <span className="mui-icon">
                  <Icon icon="Globe" size={18} />
                </span>
                <span className="mui-text">
                  <Trans>Public Dashboards</Trans>
                </span>
              </Link>
              <Link className="mui-menu-item" style={{ outline: 'none' }} to="/faq" onClick={() => props.toggleSidebar()}>
                <span className="mui-icon">
                  <Icon icon="Question" size={18} />
                </span>
                <span className="mui-text">
                  <Trans>Help Center</Trans>
                </span>
              </Link>
              {showTickets && (
                <Link className="mui-menu-item" style={{ outline: 'none' }} to="/tickets" onClick={() => props.toggleSidebar()}>
                  <span className="mui-icon">
                    <Icon icon="Ticket" size={18} />
                  </span>
                  <span className="mui-text">
                    <Trans>Support Tickets</Trans>
                  </span>
                </Link>
              )}
            </div>
          </div>
        )}
        <ul className={`mui-sidebar-menu${shouldAnimateTabs ? ' animate-items' : ''}`} ref={menuRef}>
          {isLoading
            ? Array.from({ length: 5 }, (_, i) => (
                <li key={`skeleton-${i}`} className="pc-item">
                  <div className="mui-menu-item-skeleton">
                    <div className="skeleton-circle" style={{ animationDelay: `${i * 0.12}s` }} />
                    <div className="skeleton-line" style={{ width: `${60 + (i % 3) * 12}%`, animationDelay: `${i * 0.12 + 0.1}s` }} />
                  </div>
                </li>
              ))
            : sidebarTabs}
        </ul>
        {!isMobile && (
          <div className="mui-sidebar-footer">
            {manageAnything && (
              <>
                <Link
                  id="sidebar-footer-settings"
                  className={`mui-menu-item ${isSettingsActive ? 'active' : ''}`}
                  style={{ outline: 'none' }}
                  to="/system-settings"
                >
                  <span className="mui-icon">
                    <Icon icon="phosphor-GearDuotone" size={18} />
                  </span>
                  <span className="mui-text mui-sidebar-text">
                    <Trans>System Settings</Trans>
                  </span>
                </Link>
                {isCollapsed && (
                  <UncontrolledTooltip
                    variant="dark"
                    placementPrefix="sidebar"
                    placement="right"
                    target="sidebar-footer-settings"
                    innerClassName="sidebar-tooltip-inner"
                    fade={false}
                  >
                    <Trans>System Settings</Trans>
                  </UncontrolledTooltip>
                )}
              </>
            )}
            <Link
              id="sidebar-footer-public-dashboards"
              className="mui-menu-item"
              style={{ outline: 'none' }}
              to={`/anonymous/${publicDashboardsTenantId}/dashboards`}
            >
              <span className="mui-icon">
                <Icon icon="phosphor-GlobeDuotone" size={18} />
              </span>
              <span className="mui-text mui-sidebar-text">
                <Trans>Public Dashboards</Trans>
              </span>
            </Link>
            {isCollapsed && (
              <UncontrolledTooltip
                variant="dark"
                placementPrefix="sidebar"
                placement="right"
                target="sidebar-footer-public-dashboards"
                innerClassName="sidebar-tooltip-inner"
                fade={false}
              >
                <Trans>Public Dashboards</Trans>
              </UncontrolledTooltip>
            )}
            <Link id="sidebar-footer-faq" className={`mui-menu-item ${isFaqActive ? 'active' : ''}`} style={{ outline: 'none' }} to="/faq">
              <span className="mui-icon">
                <Icon icon="phosphor-QuestionDuotone" size={18} />
              </span>
              <span className="mui-text mui-sidebar-text">
                <Trans>Help Center</Trans>
              </span>
            </Link>
            {isCollapsed && (
              <UncontrolledTooltip
                variant="dark"
                placementPrefix="sidebar"
                placement="right"
                target="sidebar-footer-faq"
                innerClassName="sidebar-tooltip-inner"
                fade={false}
              >
                <Trans>Help Center</Trans>
              </UncontrolledTooltip>
            )}
            {showTickets && (
              <>
                <Link id="sidebar-footer-tickets" className="mui-menu-item" style={{ outline: 'none' }} to="/tickets">
                  <span className="mui-icon">
                    <Icon icon="phosphor-TicketDuotone" size={18} />
                  </span>
                  <span className="mui-text mui-sidebar-text">
                    <Trans>Support Tickets</Trans>
                  </span>
                </Link>
                {isCollapsed && (
                  <UncontrolledTooltip
                    variant="dark"
                    placementPrefix="sidebar"
                    placement="right"
                    target="sidebar-footer-tickets"
                    innerClassName="sidebar-tooltip-inner"
                    fade={false}
                  >
                    <Trans>Support Tickets</Trans>
                  </UncontrolledTooltip>
                )}
              </>
            )}
            <div className="mui-sidebar-footer-user-row">
              <div id="sidebar-footer-user" className="mui-sidebar-user-widget">
                <SidebarUserPortalDropdown user={user} auth={auth}>
                  {!isCollapsed && (
                    <div className="mui-sidebar-user-details">
                      <span className="mui-sidebar-user-name">
                        {firstName || ''} {lastName || ''}
                      </span>
                      <small className="mui-sidebar-user-role">{getAccessProfileLabel(accessProfile)}</small>
                    </div>
                  )}
                </SidebarUserPortalDropdown>
              </div>
              {!isCollapsed && (
                <>
                  <button
                    id="sidebar-logout-btn"
                    className="mui-toggle-btn text-danger"
                    onClick={() => {
                      history.push('/login?skipRedirect=1');
                      auth.logout({ msalInstance });
                    }}
                  >
                    <Icon icon="SignOut" weight="regular" size={18} />
                  </button>

                  <UncontrolledTooltip
                    variant="dark"
                    placementPrefix="sidebar"
                    placement="top"
                    target="sidebar-logout-btn"
                    innerClassName="sidebar-tooltip-inner"
                    fade={false}
                  >
                    <Trans>Log out</Trans>
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function SidebarTabGroup({
  name,
  tabGroup,
  isCollapsed,
  selectedTab,
  setSelectedTab,
  startAnimIndex,
}: {
  name: string;
  tabGroup: TabGroup;
  isCollapsed?: boolean;
  selectedTab?: string;
  setSelectedTab: (tab: string) => void;
  startAnimIndex?: number;
}) {
  const theme = useTheme();
  const { isDarkMode } = useDarkMode();
  const [expanded, setExpanded] = useStoredState<boolean>(`sidebar_tabgroup_${name}`, 'local', false);
  const [isGroupHovered, setIsGroupHovered] = useState(false);
  const tabKeySet = useMemo(() => new Set(tabGroup.buttons.map(x => x.tag)), [tabGroup]);
  const isActive = useMemo(() => !!(selectedTab && tabKeySet.has(selectedTab)), [selectedTab, tabKeySet]);
  const spanKey = useUID('sidebar-item-');

  const hoverBg = isDarkMode ? 'rgba(255,255,255,0.06)' : `${theme.infoMain}1A`;

  return (
    <div
      className={`mui-menu-item-group ${expanded ? 'expanded' : ''}`}
      onMouseEnter={() => setIsGroupHovered(true)}
      onMouseLeave={() => setIsGroupHovered(false)}
    >
      <NavItem
        className="mui-menu-li"
        onClick={() => setExpanded((x: boolean) => !x)}
        style={{ listStyle: 'none', ...(startAnimIndex !== undefined ? { animationDelay: `${startAnimIndex * 45}ms` } : {}) }}
      >
        <span
          id={spanKey}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 16px',
            borderRadius: '6px',
            background: isGroupHovered ? hoverBg : 'transparent',
            transition: 'background 0.15s ease',
          }}
        >
          {isCollapsed ? (
            <span className="w-full" style={{ display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
              <Icon
                icon={tabGroup.icon ?? 'List'}
                style={{ fontSize: '1rem', opacity: isActive ? 1 : 0.65, color: isActive ? (isDarkMode ? 'white' : 'var(--primary)') : undefined }}
              />
              <Icon icon={expanded ? 'Minus' : 'Plus'} weight="regular" style={{ fontSize: '0.5em', opacity: 0.5 }} />
            </span>
          ) : (
            <>
              <small
                style={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontSize: '0.7em',
                  fontWeight: 600,
                  opacity: isActive ? 1 : 0.6,
                  color: isActive ? (isDarkMode ? 'white' : 'var(--primary)') : 'inherit',
                }}
              >
                {tabGroup.name}
              </small>
              <Icon icon={expanded ? 'Minus' : 'Plus'} weight="regular" style={{ fontSize: '0.7em', opacity: 0.5 }} />
            </>
          )}
        </span>
      </NavItem>
      {isCollapsed && (
        <UncontrolledTooltip
          variant="dark"
          placementPrefix="sidebar"
          innerClassName="sidebar-tooltip-inner"
          style={{ position: 'absolute', left: '16px', top: '-24px', pointerEvents: 'none', whiteSpace: 'nowrap' }}
          placement="right"
          target={spanKey}
          fade={false}
        >
          <small style={{ textTransform: 'capitalize' }}>{tabGroup.name}</small>
        </UncontrolledTooltip>
      )}
      <div style={{ overflow: 'hidden', maxHeight: expanded ? '1000px' : '0', transition: 'max-height 0.25s ease', marginTop: -1 }}>
        <div
          style={{
            background: isGroupHovered ? hoverBg : 'transparent',
            borderRadius: '6px',
            transition: 'background 0.15s ease',
          }}
        >
          {tabGroup.buttons.map((tabBtn, btnIdx) => (
            <TabEntry
              key={tabBtn.tag}
              tabKey={tabBtn.slug ?? tabBtn.tag}
              tabDef={tabBtn.def}
              isSubMenuItem
              isCollapsed={isCollapsed}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              animationIndex={startAnimIndex !== undefined ? startAnimIndex + 1 + btnIdx : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TabEntry({
  tabKey,
  tabDef,
  isSubMenuItem,
  isCollapsed,
  selectedTab,
  setSelectedTab,
  animationIndex,
}: {
  tabKey: string;
  tabDef: TabDefinition;
  isSubMenuItem?: boolean;
  isCollapsed?: boolean;
  selectedTab?: string;
  setSelectedTab: (tab: string) => void;
  animationIndex?: number;
}) {
  const { name, title, noTab, icon } = tabDef;
  const spanKey = `sidebar-item-${tabKey.replaceAll(' ', '-')}`;
  const levelFlag = isSubMenuItem ? 'is-submenu' : 'is-menu';

  return noTab ? null : (
    <React.Fragment key={tabKey}>
      <NavItem
        className={`mui-menu-li ${levelFlag}-li`}
        id={tabKey}
        style={animationIndex !== undefined ? { animationDelay: `${animationIndex * 45}ms` } : undefined}
      >
        <Link
          id={spanKey}
          to={tabDef.path ?? '#'}
          style={{ outline: 'none' }}
          className={`mui-menu-item ${selectedTab === tabKey ? 'active' : ''} ${levelFlag}-item`}
          onClick={e => {
            if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
              e.preventDefault();
              setSelectedTab(tabKey);
            }
          }}
        >
          <span className={'mui-icon'}>
            <Icon icon={icon} size={20} />
          </span>
          <span className="mui-text mui-sidebar-text">{title || name}</span>
        </Link>
      </NavItem>
      {isCollapsed && (
        <UncontrolledTooltip
          variant="dark"
          placementPrefix="sidebar"
          innerClassName="sidebar-tooltip-inner"
          style={{ position: 'absolute', left: '16px', top: '-24px', pointerEvents: 'none', whiteSpace: 'nowrap' }}
          placement="right"
          target={spanKey}
          fade={false}
        >
          {title || name}
        </UncontrolledTooltip>
      )}
    </React.Fragment>
  );
}

function useSidebarHooks({
  tabs: propTabs,
  tabGroups: propTabGroups,
  activeTab: propActiveTab,
  setActiveTab: propSetActiveTab,
  isCollapsed,
  user,
  auth,
  toggleSidebar,
  isLoading: propIsLoading,
}: SidebarProps) {
  const { i18n } = useLingui();
  const { ENABLE_JOBS_SETTINGS, ENABLE_DOCUMENTS_SETTINGS } = useConfiguration() as any;
  const isMobile = useIsMobile();
  const usePropTabs = propTabs && propSetActiveTab;
  const { permissions } = auth?.user || {};

  const history = useHistory();
  const { pathname } = useLocation();
  const { workpages, adminWorkpages, hasAdminWorkpages } = useContext(UWEContext) ?? {};
  const { workpageMatch, sidebarTab: _sidebarTab, isSettingsActive, isSystemSettingsRoute, isFaqActive } = useRouterContext();

  const [workpageTabs, adminTabGroups] = useMemo((): [SidebarTabs, Record<string, GroupDefinition> | undefined] => {
    if (workpageMatch?.isAdmin || pathname.startsWith('/system-settings')) {
      const [adminTabs, tabGroups] = getSystemSettingTabs({
        ENABLE_JOBS_SETTINGS,
        ENABLE_DOCUMENTS_SETTINGS,
        adminWorkpages: (adminWorkpages ?? []) as any,
      }) as [
        Record<string, { title?: string; icon?: string; permission?: string; jnx?: string; noTab?: boolean; group?: string; [key: string]: unknown }>,
        Record<string, GroupDefinition>
      ];

      const sidebarTabs = Object.entries(adminTabs).reduce((acc, [tab, tabDef]) => {
        if (
          !tabDef.noTab &&
          (tabDef.permission ? permissions?.[tabDef.permission] : true) &&
          (tabDef.jnx ? (new Jnx(tabDef.jnx as any) as { eval: (...args: unknown[]) => unknown }).eval(auth || {}, '') : true)
        ) {
          acc[tab] = {
            icon: tabDef.icon,
            path: `/system-settings/${tab}`,
            name: String(tabDef.title ?? tab),
            isAdmin: true,
            group: tabDef.group,
          };
        }
        return acc;
      }, {} as SidebarTabs);

      return [sidebarTabs, tabGroups];
    }

    const sidebarTabs = (workpages ?? []).reduce((acc, db) => {
      acc[db.slug as string] = { ...db.image, path: `/workflow/${db.slug}/tray`, name: db.name, order: db.order, isAdmin: db.isAdmin };
      return acc;
    }, {} as SidebarTabs);

    return [sidebarTabs, undefined];
  }, [workpages, workpageMatch, ENABLE_JOBS_SETTINGS, ENABLE_DOCUMENTS_SETTINGS, adminWorkpages, permissions, pathname, i18n.locale, auth]);

  const setSelected = useCallback(
    (newSelected: string) => {
      const obj = workpageTabs[newSelected];
      if (obj?.path) history.push(obj.path);
      if (isMobile) toggleSidebar();
    },
    [workpageTabs, history, isMobile, toggleSidebar]
  );

  const tabInfo = usePropTabs
    ? {
        tabs: propTabs ?? workpageTabs,
        sidebarTab: propActiveTab ?? _sidebarTab,
        setSelected: propSetActiveTab ?? setSelected,
        activeTabGroups: propTabGroups ?? adminTabGroups,
      }
    : {
        tabs: workpageTabs,
        sidebarTab: _sidebarTab,
        setSelected,
        activeTabGroups: adminTabGroups,
      };

  return {
    isCollapsed,
    user,
    auth,
    history,
    isLoading: propIsLoading ?? false,
    hasAdminWorkpages: hasAdminWorkpages ?? false,
    isSettingsActive,
    isSystemSettingsRoute,
    isFaqActive,
    ...tabInfo,
  };
}

export default Sidebar;
